import { Button } from "@/components/ui";
import type { ReusableMatterPhase } from "@/lib/types";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import {
  Check,
  Circle,
  Clock3,
  GripVertical,
  Users,
  UserRound,
} from "lucide-react";
import { PhaseTeamRoster } from "@/components/features/matter/overview/matter-phases/PhaseTeamRoster";
import type { ProjectPhaseDraft } from "./PhaseProjectBuilder";
import {
  getSamplePhaseStatus,
  type SamplePhaseStatus,
} from "./phase-project-sample-data";

const samplePhasePresentation: Record<
  SamplePhaseStatus,
  {
    label: string;
    progress: number;
    ring: string;
    text: string;
  }
> = {
  complete: {
    label: "Complete",
    progress: 100,
    ring: "#10b981",
    text: "text-emerald-700",
  },
  "in-progress": {
    label: "In progress",
    progress: 60,
    ring: "#f59e0b",
    text: "text-amber-800",
  },
  "not-started": {
    label: "Not started",
    progress: 0,
    ring: "#d1d5db",
    text: "text-muted-foreground",
  },
};

function SortablePhase({
  phase,
  index,
  phaseName,
  selected,
  readOnly,
  preview,
  idPrefix,
  onSelect,
  showSampleData,
  sampleStatus,
}: {
  phase: ProjectPhaseDraft;
  index: number;
  phaseName: string;
  selected: boolean;
  readOnly: boolean;
  preview: boolean;
  idPrefix: string;
  onSelect: () => void;
  showSampleData: boolean;
  sampleStatus: SamplePhaseStatus;
}) {
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: phase.localId, disabled: readOnly });
  const sample = samplePhasePresentation[sampleStatus];
  const teamLabel = phase.userId
    ? `${phase.userLabel ?? "Unavailable individual"} · ${phase.roleLabel ?? "Unavailable role"}${phase.userAvailable === false ? " (Unavailable)" : ""}`
    : phase.teamId
      ? (phase.teamLabel ?? "Assigned team")
      : "Unassigned";
  const teamSummary = (
    <span className="text-muted-foreground flex min-w-0 items-center gap-1 text-xs">
      {phase.userId ? (
        <UserRound className="size-3.5 shrink-0" aria-hidden="true" />
      ) : (
        <Users className="size-3.5 shrink-0" aria-hidden="true" />
      )}
      <span className="truncate">{teamLabel}</span>
    </span>
  );

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`min-w-[170px] flex-1 ${isDragging ? "opacity-60" : ""}`}
    >
      <div
        className={`relative flex min-h-28 min-w-0 flex-1 items-center rounded-xl border text-sm transition-colors ${
          selected
            ? "border-primary bg-muted/30 text-foreground"
            : "border-border/70 text-muted-foreground hover:bg-muted/45 hover:text-foreground"
        }`}
      >
        {!readOnly ? (
          <div className="absolute top-1 right-1 z-10">
            <Button
              ref={setActivatorNodeRef}
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`Drag to reorder ${phaseName}`}
              className="shrink-0 cursor-grab active:cursor-grabbing"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-4 w-4" />
            </Button>
          </div>
        ) : null}
        <button
          type="button"
          role={preview ? "tab" : undefined}
          aria-selected={preview ? selected : undefined}
          aria-controls={
            preview ? `${idPrefix}phase-${phase.localId}` : undefined
          }
          aria-current={selected ? "step" : undefined}
          onClick={onSelect}
          className="flex min-w-0 flex-1 items-center gap-3 rounded-xl px-4 py-3 pr-10 text-left"
        >
          <span
            className="relative flex size-11 shrink-0 items-center justify-center rounded-full"
            style={
              showSampleData
                ? {
                    background: `conic-gradient(${sample.ring} ${sample.progress}%, #e5e7eb ${sample.progress}% 100%)`,
                  }
                : undefined
            }
            aria-label={
              showSampleData
                ? `${sample.progress}% complete in sample data`
                : `Phase ${index + 1}`
            }
          >
            <span className="bg-background flex size-8 items-center justify-center rounded-full border text-sm">
              {!showSampleData ? (
                index + 1
              ) : sampleStatus === "complete" ? (
                <Check className="size-4 text-emerald-600" aria-hidden="true" />
              ) : sampleStatus === "in-progress" ? (
                <Clock3 className="size-4 text-amber-600" aria-hidden="true" />
              ) : (
                <Circle
                  className="text-muted-foreground size-3.5"
                  aria-hidden="true"
                />
              )}
            </span>
          </span>
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="font-medium break-words" title={phaseName}>
                {phaseName}
              </span>
            </span>
            {showSampleData ? (
              <span className={`text-xs ${sample.text}`}>{sample.label}</span>
            ) : null}
            <span className="text-muted-foreground text-xs">
              {phase.actionDefinitionIds.length}{" "}
              {phase.actionDefinitionIds.length === 1 ? "action" : "actions"}
              {" · "}
              {phase.widgets.length}{" "}
              {phase.widgets.length === 1 ? "widget" : "widgets"}
            </span>
            {phase.teamId ? (
              <PhaseTeamRoster
                teamId={phase.teamId}
                hoverTrigger={teamSummary}
              />
            ) : (
              teamSummary
            )}
          </span>
        </button>
      </div>
    </div>
  );
}

export function PhaseProjectPhaseNavigation({
  phases,
  reusablePhases,
  activePhaseId,
  readOnly,
  preview,
  idPrefix,
  onSelect,
  onReorder,
  showSampleData,
}: {
  phases: ProjectPhaseDraft[];
  reusablePhases: ReusableMatterPhase[];
  activePhaseId?: string;
  readOnly: boolean;
  preview: boolean;
  idPrefix: string;
  onSelect: (phaseId: string) => void;
  onReorder: (event: DragEndEvent) => void;
  showSampleData: boolean;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  return (
    <nav
      aria-label={preview ? "Phase preview" : "Project phases"}
      role={preview ? "tablist" : undefined}
      className="min-w-0"
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={onReorder}
      >
        <SortableContext
          items={phases.map((phase) => phase.localId)}
          strategy={horizontalListSortingStrategy}
        >
          <div className="flex gap-3 overflow-x-auto py-1">
            {phases.map((phase, index) => (
              <SortablePhase
                key={phase.localId}
                phase={phase}
                index={index}
                phaseName={
                  reusablePhases.find(
                    (item) => item.id === phase.reusablePhaseId,
                  )?.title ?? "Unavailable Phase"
                }
                selected={activePhaseId === phase.localId}
                readOnly={readOnly}
                preview={preview}
                idPrefix={idPrefix}
                onSelect={() => {
                  onSelect(phase.localId);
                }}
                showSampleData={showSampleData}
                sampleStatus={getSamplePhaseStatus(index, phases.length)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </nav>
  );
}
